/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnDestroy, ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { I18nService } from '@dm/core';
import { eventType, infigo } from '@dm/live-material-infigo';
import { QuickStartService } from 'app/feature/auth/quick-start/quick-start.service';

import { InfigoService } from '../../infigo.service';

@Component({
    selector: 'app-carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
})
export class CarouselComponent implements OnDestroy, AfterViewInit {
    @Input() list;
    @Input() delay = 10000;
    @Input() interval = 10000;
    @ViewChild('container', { static: true }) container;
    activeIndex = 0;
    actualIndex = 0;
    timer;
    jumpPath;
    animationDuration = 1000;

    constructor(
        public changeDetectorRef: ChangeDetectorRef,
        private infigoService: InfigoService,
        private quickStartService: QuickStartService,
        public i18n: I18nService,
    ) { }

    ngOnDestroy(): void {
        this.stop();
        document.removeEventListener('visibilitychange', this.visibleChange);
        this.container.nativeElement.removeEventListener('transitionend', this.transitionEnd);
    }

    nextPicture(): void {
        this.setIndex(this.activeIndex + 1);
        this.timer = setTimeout(() => this.nextPicture(), this.interval + this.animationDuration);
    }

    transitionEnd = (): void => {
        if (this.actualIndex >= this.list.length) {
            this.container.nativeElement.style.transitionDuration = '0s';
            this.container.nativeElement.style.transform = 'translateX(0%)';
            setTimeout(() => this.container.nativeElement.style.transitionDuration = '1s');
        }
    };
    visibleChange = (): void => {
        if (document.hidden) {
            this.stop();
        } else {
            this.reset();
        }
    };

    ngAfterViewInit(): void {
        document.addEventListener('visibilitychange', this.visibleChange);
        this.container.nativeElement.addEventListener('transitionend', this.transitionEnd);
        if (this.list && this.list.length > 1) {
            this.timer = setTimeout(() => this.nextPicture(), this.interval + this.animationDuration + this.delay);
        }
    }

    setIndex(activeIndex: number): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.actualIndex = activeIndex;
        if (activeIndex < this.list.length) {
            this.activeIndex = activeIndex;
            this.container.nativeElement.style.transform = `translateX(${-1 * 100 * activeIndex}%)`;
            this.changeDetectorRef.detectChanges();
        } else {
            this.activeIndex = 0;
            this.container.nativeElement.style.transform = `translateX(${-1 * 100 * activeIndex}%)`;
            this.changeDetectorRef.detectChanges();
        }
    }

    reset(): void {
        if (!this.timer) {
            this.timer = setTimeout(() => this.nextPicture(), this.interval + this.animationDuration);
        }
    }

    stop(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    jumpToNewPage(value: string): void {
        const url = this.quickStartService.getQuickStartUrl(value);
        window.open(url, '_self');
    }

    setRouterPath(value: { routeType: any }): void {
        this.jumpPath = {
            id: '',
            name: '',
            precisionType: value.routeType,
            COMBINATION: '',
        };
        const routerEvent = this.infigoService.transformSearchParams(this.jumpPath);
        infigo.dispatch(eventType.CLICK_LIST_ITEM, routerEvent);
    }
}
